import { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import EmojiPicker from "emoji-picker-react";
import DOMPurify from "dompurify";
import { ImageIcon, Smile, Bold, Italic, Save } from "lucide-react";

interface Props {
  value: string;
  onSave: (safeHtml: string, json: any) => void;
}

export default function MyTipTapEditor({ value, onSave }: Props) {
  const [dirty, setDirty] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ allowBase64: true }),
    ],
    content: value,
    onUpdate: () => setDirty(true),
  });

  // Sync external value → editor
  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(value, false as any);
    setDirty(false);
  }, [value, editor]);

  if (!editor) return null;

  /* ---------------- SAVE HANDLER ---------------- */

  const handleSave = () => {
  const rawHtml = editor.getHTML();
  const json = editor.getJSON();

  const safeHtml = DOMPurify.sanitize(rawHtml, {
    USE_PROFILES: { html: true },
  });

  setDirty(false);
  onSave(safeHtml, json);
};

  const addImage = () => {
    const url = window.prompt("Image URL");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addEmoji = (emoji: any) => {
    editor.chain().focus().insertContent(emoji.emoji).run();
    setShowEmoji(false);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* TOOLBAR */}
      <div className="flex items-center gap-1 border-b p-2 bg-gray-100">

        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold size={16} />
        </ToolbarButton>

        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={addImage}
        >
          <ImageIcon size={16} />
        </ToolbarButton>

         {/* EMOJI */}
        <ToolbarButton onClick={() => setShowEmoji(v => !v)}>
          <Smile size={16} />
        </ToolbarButton>

        {showEmoji && (
          <div className="absolute z-50 mt-10">
            <EmojiPicker onEmojiClick={addEmoji} />
          </div>
        )}

        {/* SAVE */}
        <div className="ml-auto">
          <button
            disabled={!dirty}
            onClick={handleSave}
            className={`
              flex items-center gap-1 px-3 py-1.5 rounded text-sm
              ${dirty
                ? "bg-black text-white hover:bg-gray-800"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"}
            `}
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>

      {/* EDITOR */}
      <EditorContent editor={editor} className="p-3 min-h-[220px]" />
    </div>
  );
}

/* ---------------------------------- */
/* Toolbar Button */
/* ---------------------------------- */

function ToolbarButton({
  children,
  onClick,
  active,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        p-2 rounded
        ${active ? "bg-black text-white" : "bg-white hover:bg-gray-200"}
      `}
    >
      {children}
    </button>
  );
}